/**
 * CREDIT A SUBSET OF AN INVOICE LINE CITIES, PRO RATA (owner 2026-10-27).
 *
 * IDENTICAL FILE in emerge-backend and emerge-frontend (`src/lib/credit-city-pro-rata.ts`).
 * Change both or neither. No imports, so it compiles unchanged in either repo.
 *
 * A credit line for n of the N cities on an invoice line keeps the invoice
 * line quantity, discount % and tax, and scales the money:
 *
 *   unitPrice      = round2(invoice unit_price      x n / N)
 *   discountAmount = round2(invoice discount_amount x n / N)   (null stays null)
 *
 * round2 is half-up on the magnitude (half away from zero) to cents, so a
 * negative line rounds symmetrically. n = N returns both inputs AS GIVEN (the
 * whole line, unchanged). The result is a prefill: the amount stays editable.
 *
 * Exact BigInt maths on decimal strings. `Number()` / `parseFloat` never touch
 * the money. Throws RangeError when lineCities < 1, creditedCities < 1 or
 * creditedCities > lineCities (or either is not an integer), and TypeError
 * when a money value is not a decimal string.
 *
 * 6-city line at 30,000.00, 2 cities credited: 10,000.00.
 * 3-city line at 100.00, 1 city credited: 33.33. 2 of 3: 66.67.
 */

export interface CreditCityProRataInput {
  /** The invoice line `unit_price` as a decimal string. */
  unitPrice: string;
  /** The invoice line `discount_amount` as a decimal string, or null when unset. */
  discountAmount: string | null;
  /** Number of cities on the invoice line (N). */
  lineCities: number;
  /** Number of those cities this credit line credits (n). */
  creditedCities: number;
}

export interface CreditCityProRataResult {
  unitPrice: string;
  discountAmount: string | null;
}

const CREDIT_DECIMAL_PATTERN = /^(-?)(\d+)(?:\.(\d+))?$/;

function parseCreditDecimal(value: string, field: string): { units: bigint; scale: number } {
  const match = CREDIT_DECIMAL_PATTERN.exec(value.trim());
  if (!match) {
    throw new TypeError(`credit-city-pro-rata: ${field} is not a decimal string: ${JSON.stringify(value)}`);
  }
  const [, sign, whole = '0', fraction = ''] = match;
  const magnitude = BigInt(whole + fraction);
  return { units: sign === '-' ? -magnitude : magnitude, scale: fraction.length };
}

function scaleToCents(value: string, field: string, credited: number, total: number): string {
  const parsed = parseCreditDecimal(value, field);
  // value x n / N in cents = units x n x 100 / (10^scale x N), half away from zero.
  const numerator = parsed.units * BigInt(credited) * 100n;
  const denominator = 10n ** BigInt(parsed.scale) * BigInt(total);
  const negative = numerator < 0n;
  const magnitude = negative ? -numerator : numerator;
  const cents = (magnitude * 2n + denominator) / (denominator * 2n);
  const fraction = (cents % 100n).toString().padStart(2, '0');
  return `${negative && cents !== 0n ? '-' : ''}${cents / 100n}.${fraction}`;
}

/** The prefilled money of a credit line crediting `creditedCities` of `lineCities`. */
export function proRataCreditLine(input: CreditCityProRataInput): CreditCityProRataResult {
  const { unitPrice, discountAmount, lineCities, creditedCities } = input;
  if (!Number.isSafeInteger(lineCities) || lineCities < 1) {
    throw new RangeError(`credit-city-pro-rata: lineCities must be an integer of at least 1, got ${String(lineCities)}`);
  }
  if (!Number.isSafeInteger(creditedCities) || creditedCities < 1 || creditedCities > lineCities) {
    throw new RangeError(
      `credit-city-pro-rata: creditedCities must be an integer from 1 to ${String(lineCities)}, got ${String(creditedCities)}`,
    );
  }
  if (creditedCities === lineCities) {
    // Validate even on the pass-through, so a bad value never slips out unchecked.
    parseCreditDecimal(unitPrice, 'unitPrice');
    if (discountAmount !== null) parseCreditDecimal(discountAmount, 'discountAmount');
    return { unitPrice, discountAmount };
  }
  return {
    unitPrice: scaleToCents(unitPrice, 'unitPrice', creditedCities, lineCities),
    discountAmount:
      discountAmount === null ? null : scaleToCents(discountAmount, 'discountAmount', creditedCities, lineCities),
  };
}
